import * as fromAssignmentsActions from "#core/teacher-classes/store/actions/assignments.action";
import { AssignmentPagination, AssignmentPaginationParameters } from "@carnegie-learning/data-access-angular-passport";
import { Action, createReducer, on } from "@ngrx/store";
import { PPAssignment as Assignment } from "cli-types";

export interface AssignmentsListState {
    entities: Map<number, Assignment>;
    loading: boolean;
    loaded: boolean;
    needsEvalLoading: boolean; // for Teacher Dashboard
    needsEvalLoaded: boolean;
    currentAssignment: Assignment | null;
    rawResponse: AssignmentPagination | null;
    coursePaginationParams: AssignmentPaginationParameters;
    studentPaginationParams: AssignmentPaginationParameters;
    courseAssignmentsFilterBoxOpened: boolean;
    studentAssignmentsFilterBoxOpened: boolean;
}

export const initialState: AssignmentsListState = {
    entities: new Map<number, Assignment>(),
    loading: false,
    loaded: false,
    needsEvalLoading: false,
    needsEvalLoaded: false,
    currentAssignment: null,
    rawResponse: null,
    coursePaginationParams: {
        studentID: null,
        courseID: null,
        filter: { dateRanges: [], status: [], assignableType: [] },
        limit: 25,
        page: 0,
        search: "",
        sorting: { dueDate: "asc" },
        withCourseMetrics: true,
    },
    studentPaginationParams: {
        studentID: null,
        courseID: null,
        filter: { status: [], assignableType: [] },
        limit: 25,
        page: 0,
        search: "",
        sorting: { dueDate: "asc" },
    },
    courseAssignmentsFilterBoxOpened: false,
    studentAssignmentsFilterBoxOpened: false,
};

const assignmentsReducer = createReducer(
    initialState,
    on(fromAssignmentsActions.LoadEvalAssignments, (state, { payload }) => {
        return {
            ...state,
            loading: true,
            loaded: false,
            needsEvalLoading: false,
            studentPaginationParams: payload,
            needsEvalLoaded: false,
        };
    }),
    on(fromAssignmentsActions.LoadCourseAssignments, (state, { payload }) => {
        return {
            ...state,
            loading: true,
            loaded: false,
            needsEvalLoading: false,
            coursePaginationParams: payload,
            needsEvalLoaded: false,
            entities: new Map<number, Assignment>(),
        };
    }),
    on(fromAssignmentsActions.LoadNeedEvalAssignments, (state, {}) => {
        return {
            ...state,
            loading: false,
            loaded: false,
            needsEvalLoading: true,
            needsEvalLoaded: false,
        };
    }),
    on(
        fromAssignmentsActions.LoadCourseAssignmentsSuccess,
        fromAssignmentsActions.LoadEvalAssignmentsSuccess,
        (state, { payload }) => {
            const assignments = (<AssignmentPagination>payload).data;

            const entities = new Map<number, Assignment>();
            assignments.forEach((assignment) => {
                entities.set(assignment.assignmentID, assignment);
            });

            // then re-bind the entity const with our returned state
            return {
                ...state,
                loading: false,
                loaded: true,
                needsEvalLoading: false,
                needsEvalLoaded: false,
                entities: entities,
                rawResponse: <AssignmentPagination>payload,
            };
        }
    ),
    on(
        fromAssignmentsActions.LoadNeedEvalAssignmentsSuccess,
        (state, { payload }) => {
            const assignments = (<AssignmentPagination>payload).data;

            const entities = new Map<number, Assignment>();
            assignments.forEach((assignment) => {
                entities.set(assignment.assignmentID, assignment);
            });

            return {
                ...state,
                loading: false,
                loaded: false,
                needsEvalLoading: false,
                needsEvalLoaded: true,
                entities: entities,
                rawResponse: <AssignmentPagination>payload,
            };
        }
    ),
    on(
        fromAssignmentsActions.LoadNeedEvalAssignmentsFail,
        fromAssignmentsActions.LoadCourseAssignmentsFail,
        fromAssignmentsActions.LoadEvalAssignmentsFail,
        (state, { payload }) => {
            return {
                ...state,
                loading: false,
                loaded: false,
                needsEvalLoading: false,
                needsEvalLoaded: false,
            };
        }
    ),
    on(
        fromAssignmentsActions.UpdateStudentProfileAssignmentSuccess,
        (state, { payload }) => {
            const assignment = payload;
            const entities = new Map<number, Assignment>(
                state.entities.entries()
            );
            // Need to keep appended keys since return payload does not contain they properties
            let currentEntities = entities.get(assignment.assignmentID);
            currentEntities = Object.assign({}, currentEntities, assignment); // merge the two without missing properties

            entities.set(assignment.assignmentID, currentEntities);

            if (
                state.currentAssignment?.assignmentID ===
                assignment.assignmentID
            ) {
                return {
                    ...state,
                    entities,
                    currentAssignment: {
                        ...state.currentAssignment,
                        ...assignment,
                    },
                };
            } else {
                return {
                    ...state,
                    entities,
                };
            }
        }
    ),
    on(
        fromAssignmentsActions.DeleteAssignmentSuccess,
        (state, { assignmentID }) => {
            const entities = new Map<number, Assignment>(
                state.entities.entries()
            );
            entities.delete(assignmentID);

            let updatedTotal;
            if (state.coursePaginationParams.total) {
                updatedTotal = state.coursePaginationParams.total - 1;
            }
            let updatedParams = {
                ...state.coursePaginationParams,
                total: updatedTotal,
            };

            return {
                ...state,
                entities: entities,
                coursePaginationParams: updatedParams,
            };
        }
    ),
    on(
        fromAssignmentsActions.SetCourseAssignmentsPaginationParams,
        (state, { payload }) => {
            const coursePaginationParams = {
                ...state.coursePaginationParams,
                ...payload,
            };

            return {
                ...state,
                coursePaginationParams: coursePaginationParams,
            };
        }
    ),
    on(
        fromAssignmentsActions.SetStudentAssignmentsPaginationParams,
        (state, { payload }) => {
            const studentPaginationParams = {
                ...state.studentPaginationParams,
                ...payload,
            };
            return {
                ...state,
                studentPaginationParams: studentPaginationParams,
            };
        }
    ),
    on(
        fromAssignmentsActions.LoadAssignmentsCourseMetricsSuccess,
        (state, { assetId, courseID, shortAssignableType, courseMetrics }) => {
            let assignmentID: number | null = null;
            state.entities.forEach(function (value) {
                if (value.assetId === assetId && value.courseID === courseID) {
                    assignmentID = value.assignmentID;
                }
            });
            let updatedAssignment =
                assignmentID !== null ? state.entities.get(assignmentID) : null;
            const updatedCourseMetrics = courseMetrics;

            // Update the entities with the updatedAssignment
            const entities = new Map<number, Assignment>(
                state.entities.entries()
            );

            // Update the teacher-class with the updatedCourseMetrics
            if (updatedAssignment && assignmentID !== null) {
                updatedAssignment = {
                    ...updatedAssignment,
                    courseMetrics: updatedCourseMetrics,
                };
                entities.set(assignmentID, updatedAssignment);
            }

            return {
                ...state,
                entities: entities,
            };
        }
    ),
    on(
        fromAssignmentsActions.LoadAssignmentAssetSuccess,
        (state, { assignmentId, asset }) => {
            const entities = new Map<number, Assignment>(
                state.entities.entries()
            );

            let assignment = entities.get(assignmentId);
            if (asset && assignment) {
                assignment = { ...assignment, asset: asset };
                entities.set(assignmentId, assignment);
            }

            let updatedCurrentAssignment = state.currentAssignment;
            if (
                asset &&
                updatedCurrentAssignment &&
                updatedCurrentAssignment.assignmentID === assignmentId
            ) {
                updatedCurrentAssignment = {
                    ...updatedCurrentAssignment,
                    asset: asset,
                };
            }

            return {
                ...state,
                entities: entities,
                currentAssignment: updatedCurrentAssignment,
            };
        }
    ),
    on(
        fromAssignmentsActions.LoadAllAssignmentAssetsSuccess,
        (state, { assignments, assets }) => {
            const entities = new Map<number, Assignment>(
                state.entities.entries()
            );

            if (assignments && assets) {
                assignments.forEach((assignment) => {
                    const asset = assets.find(
                        (asset) => asset.assetId === assignment.assetId
                    );
                    assignment = { ...assignment, asset: asset };

                    if (
                        assignment.compositeTaskAssignments &&
                        assignment.compositeTaskAssignments.length > 0
                    ) {
                        const newCompositeTaskAssignments =
                            assignment.compositeTaskAssignments.map((task) => {
                                const asset = assets.find(
                                    (asset) => asset.assetId === task.assetId
                                );
                                return { ...task, asset: asset };
                            });
                        if (newCompositeTaskAssignments) {
                            assignment = {
                                ...assignment,
                                compositeTaskAssignments:
                                    newCompositeTaskAssignments,
                            };
                        }
                    }
                    entities.set(assignment.assignmentID, assignment);
                });
            }
            return {
                ...state,
                entities: entities,
            };
        }
    ),
    on(fromAssignmentsActions.LoadAllAssignmentAssetsFail, (state, { payload }) => {
        // Asset loading failed - assignments are loaded but assets failed to load
        // Mark assignments as having processed assets (even if failed) to prevent infinite spinner
        const entities = new Map<number, Assignment>(state.entities.entries());
        
        entities.forEach((assignment, id) => {
            if (!assignment.asset) {
                // Explicitly set asset to undefined to indicate failed/processed loading
                const updatedAssignment = { ...assignment, asset: undefined };
                entities.set(id, updatedAssignment);
            }
        });
        
        console.warn('Assignment assets failed to load:', payload);
        
        return {
            ...state,
            entities: entities,
        };
    }),
    on(fromAssignmentsActions.SetCurrentAssignment, (state, { payload }) => {
        return {
            ...state,
            currentAssignment: payload,
        };
    }),
    on(fromAssignmentsActions.ClearCurrentAssignment, (state, {}) => {
        return {
            ...state,
            currentAssignment: null,
        };
    }),
    on(
        fromAssignmentsActions.UpdateCourseAssignments,
        (state, { assignmentID, quickEval }) => {
            const entities = new Map<number, Assignment>(
                state.entities.entries()
            );
            if (quickEval) {
                entities.delete(assignmentID);
            }
            return {
                ...state,
                entities,
            };
        }
    ),
    // on(fromAssignmentsActions.UpdateCourseAssignment,
    //     (state,
    //      {
    //         assetId,
    //         shortAssignableType,
    //         courseID,
    //         payload
    //      }
    //     ) => {
    //     const assignments = payload;

    //     const entities = new Map<number, Assignment>(
    //         state.entities.entries()
    //     );
    // Below the const assignments doesnt match the correct type of UpdateCourseAssignmentPayload
    // and it is breaking the reducer
    // so commenting it out for now. Tested update teacher-class assignment and work as expected.
    //     assignments.forEach(assignment => {
    //         entities.set(assignment.assignmentID, assignment);
    //     });
    //     if (payload.quickEval) {
    //         entities.delete(payload.assignmentID);
    //     }

    //     return {
    //         ...state,
    //         entities: entities
    //     };
    // }),
    on(
        fromAssignmentsActions.UpdateCourseAssignmentSuccess,
        (state, { payload }) => {
            const assignments: Assignment[] = payload;

            const entities = new Map<number, Assignment>(
                state.entities.entries()
            );
            assignments.forEach((assignment) => {
                entities.set(assignment.assignmentID, assignment);
            });

            return {
                ...state,
                entities: entities,
            };
        }
    ),
    on(
        fromAssignmentsActions.SetCourseAssignmentsFilterBoxOpened,
        (state, { payload }) => {
            return {
                ...state,
                courseAssignmentsFilterBoxOpened: payload,
            };
        }
    ),
    on(fromAssignmentsActions.ResetAssignmentsStore, (state, {}) => {
        return initialState;
    }),
    on(fromAssignmentsActions.ResetAssignmentsStoreAssignments, (state, {}) => {
        return {
            ...state,
            entities: new Map<number, Assignment>(),
            currentAssignment: null,
            loaded: false,
            loading: false,
            rawResponse: null,
        };
    })
);

// exporting the reducer so it can be added to the index file
export function reducer(
    state: AssignmentsListState | undefined,
    action: Action
) {
    return assignmentsReducer(state, action);
}

// this is where we want to export some levels of state
export const getAssignmentsEntities = (state: AssignmentsListState) =>
    state.entities;
export const getEvalAssignmentsLoading = (state: AssignmentsListState) =>
    state.loading;
export const getEvalAssignmentsLoaded = (state: AssignmentsListState) =>
    state.loaded;
export const getNeedsEvalAssignmentsLoading = (state: AssignmentsListState) =>
    state.needsEvalLoading;
export const getNeedsEvalAssignmentsLoaded = (state: AssignmentsListState) =>
    state.needsEvalLoaded;
export const getRawResponse = (state: AssignmentsListState) =>
    state.rawResponse;
export const getCoursePaginationParams = (state: AssignmentsListState) =>
    state.coursePaginationParams;
export const getStudentPaginationParams = (state: AssignmentsListState) =>
    state.studentPaginationParams;
export const getCurrentStudentProfileAssignment = (
    state: AssignmentsListState
) => state.currentAssignment;

export const getCourseAssignmentsFilterBoxOpened = (
    state: AssignmentsListState
) => state.courseAssignmentsFilterBoxOpened;
export const getStudentAssignmentsFilterBoxOpened = (
    state: AssignmentsListState
) => state.studentAssignmentsFilterBoxOpened;
